import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardImg,
  Badge,
  Spinner,
  Input,
  Alert,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CampaignService from "../../../services/OrderingServices/CampaignService";
import DeviceHeartbeatService from "../../../services/DeviceHeartbeatServices/DeviceHeartbeatService";
import DeviceAdsService from "../../../services/OrderingServices/DeviceAdsService";
import Select from "react-select";
import kiosk from "../../../assets/images/kiosk.png";

// ----- Main Component -----
const AssignCampaign = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setLoading(true);
      const [campaignRes, deviceRes] = await Promise.all([
        CampaignService.campaignsList({ pageSize: 100 }),
        DeviceHeartbeatService.getLatestDeviceStatus(),
      ]);

      const campaignList = campaignRes?.data || [];
      setCampaigns(campaignList);

      // Filter only active devices
      const activeDevices = Array.isArray(deviceRes)
        ? deviceRes.filter((device) => device.isActive === false)
        : [];
      setDevices(activeDevices);
    } catch (error) {
      toast.error("Failed to load data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleCampaignSelect = (option) => {
    setSelectedCampaign(option ? option.value : null);
  };

  const campaignOptions = campaigns.map((campaign) => ({
    value: campaign.id,
    label: campaign.description
      ? `${campaign.name} - ${campaign.description}`
      : campaign.name,
  }));

  const [selectedDevices, setSelectedDevices] = useState([]);

  const toggleDeviceSelection = (device) => {
    const index = selectedDevices.findIndex((d) => d.macId === device.macId);
    if (index > -1) {
      setSelectedDevices(
        selectedDevices.filter((d) => d.macId !== device.macId),
      );
    } else {
      setSelectedDevices([...selectedDevices, device]);
    }
  };

  const getDevicePosition = (macId) => {
    const index = selectedDevices.findIndex((d) => d.macId === macId);
    return index > -1 ? index + 1 : null;
  };

  const handleAssign = async () => {
    if (!selectedCampaign || selectedDevices.length === 0) {
      toast.error("Please select a campaign and at least one device");
      return;
    }
    try {
      setAssigning(true);

      // Prepare data for bulk assignment
      const assignmentData = selectedDevices.map((device, index) => ({
        orderIndex: index,
        macId: device.macId,
        campaignId: selectedCampaign, // Using campaign ID as adId for the API
      }));

      await DeviceAdsService.bulkAssignCampaigns(assignmentData);
      toast.success(
        `Successfully assigned campaign to ${selectedDevices.length} device(s)!`,
      );
      setSelectedCampaign(null);
      setSelectedDevices([]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to assign campaign");
      console.error(error);
    } finally {
      setAssigning(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedDevices([]);
  };

  const filteredDevices = devices.filter(
    (d) =>
      d.machineName?.toLowerCase().includes(deviceSearch.toLowerCase()) ||
      d.macId?.toLowerCase().includes(deviceSearch.toLowerCase()),
  );

  const getDeviceImageUrl = () => {
    // Try to load kiosk.png from public/assets or use fallback
    return kiosk;
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid className="text-center py-5">
          <Triangle color="#2c3e50" height={80} width={80} />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Assign Campaign to Devices"
          pageTitle="Campaign Management"
        />

        {/* Campaign Selection */}
        <Row className="">
          <Col lg="12">
            <Card className="border">
              <CardBody className="p-4">
                <h6 className="fw-bold mb-3">Select Campaign</h6>
                <Row className="align-items-end justify-content-between">
                  <Col md="3">
                    <Select
                      options={campaignOptions}
                      value={campaignOptions.find(
                        (option) => option.value === selectedCampaign,
                      )}
                      onChange={handleCampaignSelect}
                      placeholder="Choose a campaign..."
                      isClearable
                    />
                  </Col>
                  <Col md="4" className="text-end">
                    {selectedCampaign && (
                      <Badge color="success" pill className="px-3 py-2">
                        <i className="ri-checkbox-circle-fill me-1" />
                        Campaign Selected
                      </Badge>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Device Selection Controls */}
        {selectedCampaign && (
          <Row className="mb-4">
            <Col lg="12">
              <Alert
                color="secondary"
                className="d-flex align-items-center justify-content-between mb-0"
              >
                {/* LEFT SIDE */}
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  <i className="ri-information-line fs-4" />

                  <strong>{selectedDevices.length} device(s) selected</strong>

                  {/* Search */}
                  <div
                    className="position-relative ms-3"
                    style={{ maxWidth: "300px" }}
                  >
                    <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                    <Input
                      type="text"
                      placeholder="Search device by name or MAC ID..."
                      value={deviceSearch}
                      onChange={(e) => setDeviceSearch(e.target.value)}
                      className="ps-5"
                    />
                  </div>
                </div>

                {/* RIGHT SIDE BUTTONS */}
                <div className="d-flex align-items-center gap-2">
                  {selectedDevices.length > 0 && (
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      onClick={handleClearSelection}
                    >
                      <i className="ri-close-circle-line me-1" />
                      Clear
                    </Button>
                  )}

                  <Button
                    color="dark"
                    size="sm"
                    onClick={handleAssign}
                    disabled={assigning || selectedDevices.length === 0}
                  >
                    {assigning ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Assigning...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-1" />
                        Assign ({selectedDevices.length})
                      </>
                    )}
                  </Button>
                </div>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Device Grid */}
        {selectedCampaign && (
          <Row className="g-3">
            {filteredDevices.length === 0 ? (
              <Col lg="12">
                <Card className="border">
                  <CardBody className="text-center py-5">
                    <i className="ri-inbox-line display-1 text-muted" />
                    <h5 className="mt-3 text-muted">No active devices found</h5>
                    <p className="text-muted small">
                      Try adjusting your search or ensure devices are active
                    </p>
                  </CardBody>
                </Card>
              </Col>
            ) : (
              filteredDevices.map((device) => {
                const position = getDevicePosition(device.macId);
                const isSelected = position !== null;

                return (
                  <Col
                    key={device.macId}
                    xs="12"
                    sm="6"
                    md="4"
                    lg="3"
                    xl="2"
                    className="d-flex"
                  >
                    <Card
                      className={`w-100 border ${
                        isSelected ? "border-primary shadow-sm" : ""
                      }`}
                      onClick={() => toggleDeviceSelection(device)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="position-relative">
                        <CardImg
                          top
                          src={getDeviceImageUrl()}
                          alt={device.machineName}
                          style={{
                            height: "150px",
                            objectFit: "contain",
                            background: "#f6f8fa",
                          }}
                          onError={(e) => {
                            e.target.src =
                              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f6f8fa'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23959da5' font-family='sans-serif' font-size='14'%3EDevice%3C/text%3E%3C/svg%3E";
                          }}
                        />

                        {isSelected && (
                          <div className="position-absolute top-0 end-0 m-2">
                            <Badge color="primary" pill className="px-2 py-1">
                              {position}
                            </Badge>
                          </div>
                        )}

                        {isSelected && (
                          <div className="position-absolute bottom-0 end-0 m-2">
                            <Badge color="success" pill className="px-2 py-1">
                              <i className="ri-check-line me-1" />
                              Selected
                            </Badge>
                          </div>
                        )}

                        <div className="position-absolute bottom-0 start-0 m-2">
                          <Badge
                            color={device.isActive ? "success" : "danger"}
                            className="text-white"
                          >
                            {device.status}
                          </Badge>
                        </div>
                      </div>

                      <CardBody className="p-3">
                        <h6 className="mb-2 text-truncate fw-semibold">
                          {device.machineName}
                        </h6>
                        <div className="small text-muted">
                          <div className="mb-1">
                            <i className="ri-mac-line me-1" />
                            {device.macId}
                          </div>
                          {/* <div className="mb-1">
                            <i className="ri-router-line me-1" />
                            {device.localIp}
                          </div>
                          <div>
                            <i className="ri-time-line me-1" />
                            {new Date(device.heartbeatAt).toLocaleDateString()}
                          </div> */}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })
            )}
          </Row>
        )}

        {/* Empty State - No Campaign Selected */}
        {!selectedCampaign && (
          <Row>
            <Col lg="12">
              <Card className="border">
                <CardBody className="text-center py-5">
                  <i className="ri-folder-3-line display-1 text-muted" />
                  <h4 className="mt-4 fw-bold">Select a Campaign to Begin</h4>
                  <p className="text-muted">
                    Choose a campaign from the dropdown above to start assigning
                    it to active devices
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default AssignCampaign;
